import { useState, useEffect, useCallback } from "react";
import { API, axios } from "@/api";
import { Loader2, ChevronRight, ArrowLeft, MapPin } from "lucide-react";

const SIGNAL_STYLE = {
  high_demand: { bg: "bg-red-50", border: "border-red-200", badge: "text-red-600", label: "High Demand" },
  balanced: { bg: "bg-green-50", border: "border-green-200", badge: "text-green-600", label: "Balanced" },
  oversupplied: { bg: "bg-blue-50", border: "border-blue-200", badge: "text-blue-600", label: "Oversupplied" },
  no_supply: { bg: "bg-amber-50", border: "border-amber-200", badge: "text-amber-600", label: "No Supply" },
};

export default function GeographicDemandPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [level, setLevel] = useState("state");
  const [stateFilter, setStateFilter] = useState(null);
  const [countyFilter, setCountyFilter] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([{ label: "All States", level: "state" }]);

  const fetchData = useCallback(async (lvl, state, county) => {
    setLoading(true);
    try {
      let url = `${API}/admin/geographic-demand?level=${lvl}`;
      if (state) url += `&state=${encodeURIComponent(state)}`;
      if (county) url += `&county=${encodeURIComponent(county)}`;
      const res = await axios.get(url);
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchData("state", null, null);
  }, [fetchData]);

  const drillDown = (location) => {
    // Don't drill into aggregate entries
    if (location === "National" || location === "Multiple/National" || location.includes("County TBD")) return;
    if (level === "state") {
      setStateFilter(location);
      setLevel("county");
      setBreadcrumb([
        { label: "All States", level: "state" },
        { label: location, level: "county" },
      ]);
      fetchData("county", location, null);
    } else if (level === "county") {
      setCountyFilter(location);
      setLevel("city");
      setBreadcrumb([
        { label: "All States", level: "state" },
        { label: stateFilter, level: "county" },
        { label: location, level: "city" },
      ]);
      fetchData("city", stateFilter, location);
    }
  };

  const navigateTo = (crumb) => {
    if (crumb.level === "state") {
      setLevel("state");
      setStateFilter(null);
      setCountyFilter(null);
      setBreadcrumb([{ label: "All States", level: "state" }]);
      fetchData("state", null, null);
    } else if (crumb.level === "county") {
      setLevel("county");
      setCountyFilter(null);
      setBreadcrumb(breadcrumb.slice(0, 2));
      fetchData("county", stateFilter, null);
    }
  };

  const canDrillDown = level !== "city";

  return (
    <div className="space-y-4" data-testid="geographic-demand-panel">
      {/* Breadcrumb + Back Button */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="geo-breadcrumb">
        {breadcrumb.length > 1 && (
          <button
            onClick={() => navigateTo(breadcrumb[breadcrumb.length - 2])}
            className="flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 text-xs font-medium transition"
            data-testid="geo-back-btn"
          >
            <ArrowLeft className="w-3 h-3" /> Back
          </button>
        )}
        {breadcrumb.map((crumb, i) => (
          <span key={i} className="flex items-center gap-1">
            {i > 0 && <ChevronRight className="w-3 h-3 text-slate-300" />}
            <button
              onClick={() => navigateTo(crumb)}
              className={`text-xs font-medium ${i === breadcrumb.length - 1 ? "text-sky-600 font-bold" : "text-slate-400 hover:text-slate-600"}`}
              data-testid={`geo-crumb-${crumb.level}`}
            >
              {crumb.label}
            </button>
          </span>
        ))}
      </div>

      {/* Summary */}
      {data && (
        <div className="flex gap-2 text-xs">
          <span className="px-2 py-1 rounded-lg bg-slate-100 text-slate-600">{data.total_locations} {level}s</span>
          {data.drill_down_available && (
            <span className="px-2 py-1 rounded-lg bg-sky-50 text-sky-600">Click to drill into {data.drill_down_available}</span>
          )}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : !data || data.locations.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-4">No geographic data at this level</p>
      ) : (
        <div className="space-y-1.5 max-h-80 overflow-y-auto" data-testid="geo-list">
          {data.locations.map((row) => {
            const style = SIGNAL_STYLE[row.signal] || SIGNAL_STYLE.balanced;
            const isAggregate = row.location === "National" || row.location === "Multiple/National" || row.location.includes("County TBD");
            const isDrillable = canDrillDown && !isAggregate;
            return (
              <button
                key={row.location}
                onClick={() => isDrillable ? drillDown(row.location) : null}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left transition ${style.bg} ${style.border} ${isDrillable ? "hover:opacity-80 cursor-pointer" : "cursor-default"}`}
                data-testid={`geo-row-${row.location}`}
              >
                <MapPin className={`w-4 h-4 flex-shrink-0 ${style.badge}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800">{row.location}</p>
                  <p className="text-[10px] text-slate-400">
                    {row.users} users &middot; {row.searches} searches &middot; {row.claims} claims &middot; {row.deal_supply} deals
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className={`text-xs font-bold ${style.badge}`}>{style.label}</p>
                  <p className="text-[10px] text-slate-400">Gap: {row.demand_supply_gap}x</p>
                </div>
                {isDrillable && <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
